import { createContext, useContext, useState, useCallback } from 'react';

const CurrencyContext = createContext(null);

// Static exchange rates (will be replaced by live API rates when affiliate keys are connected)
const RATES = {
  USD: { USD: 1, GBP: 0.79, AUD: 1.53, EUR: 0.92 },
  GBP: { USD: 1.27, GBP: 1, AUD: 1.94, EUR: 1.17 },
  AUD: { USD: 0.65, GBP: 0.52, AUD: 1, EUR: 0.60 },
  EUR: { USD: 1.09, GBP: 0.86, AUD: 1.67, EUR: 1 },
};

const SYMBOLS = { USD: '$', GBP: '\u00A3', AUD: 'A$', EUR: '\u20AC' };
const LABELS = { USD: 'USD ($)', GBP: 'GBP (\u00A3)', AUD: 'AUD (A$)', EUR: 'EUR (\u20AC)' };
const CURRENCIES = ['USD', 'GBP', 'AUD', 'EUR'];

// Currency code → SoleFinder region code (the catalog uses US / UK / AU).
// EUR → UK as the closest fallback (we don't yet stock EU-specific stores).
const CURRENCY_TO_REGION = { USD: 'US', GBP: 'UK', AUD: 'AU', EUR: 'UK' };

// Best-effort currency detection from the browser's IANA timezone.
// Free, instant, zero network calls. ~95% accurate for AU/UK/US/EU.
function detectCurrencyFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (tz.startsWith('Australia/') || tz === 'Pacific/Auckland') return 'AUD';
    if (tz.startsWith('Europe/London') || tz === 'Europe/Belfast' || tz === 'Europe/Dublin') return 'GBP';
    if (tz.startsWith('Europe/')) return 'EUR';
    if (tz.startsWith('America/')) return 'USD';
    // Locale fallback for everything else
    const locale = navigator.language || '';
    if (locale.endsWith('-AU')) return 'AUD';
    if (locale.endsWith('-GB')) return 'GBP';
  } catch (e) { /* SSR safety */ }
  return 'USD';
}

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem('solefinder_currency') || detectCurrencyFromTimezone();
  });

  const changeCurrency = useCallback((c) => {
    setCurrency(c);
    localStorage.setItem('solefinder_currency', c);
  }, []);

  const convert = useCallback((amount, fromCurrency) => {
    if (!amount || !fromCurrency) return amount;
    if (fromCurrency === currency) return amount;
    const rate = RATES[fromCurrency]?.[currency] || 1;
    return Math.round(amount * rate * 100) / 100;
  }, [currency]);

  const format = useCallback((amount, fromCurrency) => {
    const converted = convert(amount, fromCurrency || 'USD');
    return `${SYMBOLS[currency] || '$'}${converted.toFixed(2)}`;
  }, [currency, convert]);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency: changeCurrency, convert, format, symbol: SYMBOLS[currency], currencies: CURRENCIES, labels: LABELS, region: CURRENCY_TO_REGION[currency] || 'US' }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}
